import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  FileText, 
  ArrowLeft, 
  Loader2,
  Calendar,
  Building2,
  Package,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import { salesOrderReportApiService, SalesOrderPerformanceDto } from '../services/salesOrderReportApi';
import { Badge } from './ui/badge';

const SalesOrderPerformanceDetailPage: React.FC = () => {
  const { salesOrderId } = useParams<{ salesOrderId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [detailData, setDetailData] = useState<SalesOrderPerformanceDto[]>([]);
  const [salesOrderInfo, setSalesOrderInfo] = useState<SalesOrderPerformanceDto | null>(null);

  useEffect(() => {
    if (salesOrderId) {
      loadDetailData();
    }
  }, [salesOrderId]);

  const loadDetailData = async () => {
    if (!salesOrderId) {
      toast.error('Sales Order ID is required');
      return;
    }

    try {
      setLoading(true);
      const response = await salesOrderReportApiService.getSalesOrderDetail(parseInt(salesOrderId));
      
      if (response.success && response.data && response.data.length > 0) {
        setDetailData(response.data);
        // Set the first item as the sales order info (header data)
        setSalesOrderInfo(response.data[0]);
      } else {
        toast.error('No data found for this sales order');
        setDetailData([]);
        setSalesOrderInfo(null);
      }
    } catch (error) {
      // console.error('Error loading sales order detail:', error);
      toast.error('Failed to load sales order detail');
      setDetailData([]);
      setSalesOrderInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | undefined | null): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  const formatDateTime = (dateString: string | undefined | null): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatNumber = (value: number | undefined | null): string => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return value.toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  const getStatusColor = (status: string | undefined | null): string => {
    if (!status) return 'bg-gray-100 text-gray-700';
    const statusLower = status.toLowerCase();
    if (statusLower.includes('completed') || statusLower.includes('delivered')) {
      return 'bg-green-100 text-green-700';
    }
    if (statusLower.includes('pending') || statusLower.includes('in progress')) {
      return 'bg-yellow-100 text-yellow-700';
    }
    if (statusLower.includes('cancelled') || statusLower.includes('rejected')) {
      return 'bg-red-100 text-red-700';
    }
    return 'bg-blue-100 text-blue-700';
  };

  const getCompletionColor = (percentage: number | undefined | null): string => {
    if (percentage === null || percentage === undefined) return 'text-gray-600';
    if (percentage >= 90) return 'text-green-600';
    if (percentage >= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  // Calculate totals
  const totals = detailData.reduce((acc, item) => {
    acc.orderedQuantity = (acc.orderedQuantity || 0) + (item.orderedQuantity || 0);
    acc.packedQuantity = (acc.packedQuantity || 0) + (item.packedQuantity || 0);
    acc.wipQuantity = (acc.wipQuantity || 0) + (item.wipQuantity || 0);
    acc.invoicedQuantity = (acc.invoicedQuantity || 0) + (item.invoicedQuantity || 0);
    acc.pendingQuantity = (acc.pendingQuantity || 0) + (item.pendingQuantity || 0);
    return acc;
  }, {
    orderedQuantity: 0,
    packedQuantity: 0,
    wipQuantity: 0,
    invoicedQuantity: 0,
    pendingQuantity: 0
  });

  const overallCompletion = (totals.orderedQuantity || 0) > 0
    ? (((totals.packedQuantity || 0) + (totals.invoicedQuantity || 0)) / (totals.orderedQuantity || 1)) * 100
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100/50 p-6">
      <div className="space-y-6">
        {/* Header */}
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-primary-200 flex items-center justify-center">
                  <FileText className="h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-2xl">
                    Sales Order Detail - {salesOrderInfo?.salesOrderNumber || 'N/A'}
                  </CardTitle>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.close()}
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Close
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Loading State */}
        {loading ? (
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600 mr-3" />
                <span className="text-gray-600">Loading sales order details...</span>
              </div>
            </CardContent>
          </Card>
        ) : !salesOrderInfo ? (
          <Card className="border-primary-200 bg-white shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-600">No data found for this sales order</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Sales Order Information */}
            <Card className="border-primary-200 bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-gray-800">
                  Sales Order Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FileText className="h-4 w-4" />
                      <span>Sales Order Number</span>
                    </div>
                    <div className="text-lg font-semibold text-gray-800">
                      {salesOrderInfo.salesOrderNumber || 'N/A'}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>Order Date</span>
                    </div>
                    <div className="text-lg font-semibold text-gray-800">
                      {formatDate(salesOrderInfo.orderDate)}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span>Status</span>
                    </div>
                    <div>
                      <Badge className={getStatusColor(salesOrderInfo.salesOrderStatus)}>
                        {salesOrderInfo.salesOrderStatus || 'N/A'}
                      </Badge>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Building2 className="h-4 w-4" />
                      <span>Branch</span>
                    </div>
                    <div className="text-lg font-semibold text-gray-800">
                      {salesOrderInfo.branchName || 'N/A'} ({salesOrderInfo.branchCode || 'N/A'})
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <User className="h-4 w-4" />
                      <span>Customer</span>
                    </div>
                    <div className="text-lg font-semibold text-gray-800">
                      {salesOrderInfo.customerName || 'N/A'} ({salesOrderInfo.customerCode || 'N/A'})
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>Last Activity</span>
                    </div>
                    <div className="text-lg font-semibold text-gray-800">
                      {formatDateTime(salesOrderInfo.lastActivityDate)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Performance Summary */}
            <Card className="border-primary-200 bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-gray-800">
                  Performance Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="space-y-2">
                    <div className="text-sm text-gray-600">Total Ordered</div>
                    <div className="text-2xl font-bold text-gray-800">
                      {formatNumber(totals.orderedQuantity)}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm text-gray-600">Packed</div>
                    <div className="text-2xl font-bold text-blue-600">
                      {formatNumber(totals.packedQuantity)}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm text-gray-600">WIP</div>
                    <div className="text-2xl font-bold text-yellow-600">
                      {formatNumber(totals.wipQuantity)}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm text-gray-600">Invoiced</div>
                    <div className="text-2xl font-bold text-green-600">
                      {formatNumber(totals.invoicedQuantity)}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm text-gray-600">Pending</div>
                    <div className="text-2xl font-bold text-red-600">
                      {formatNumber(totals.pendingQuantity)}
                    </div>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">Overall Completion</div>
                    <div className={`text-2xl font-bold ${getCompletionColor(overallCompletion)}`}>
                      {overallCompletion.toFixed(2)}%
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Detail Items Table */}
            <Card className="border-primary-200 bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl font-semibold text-gray-800">
                  Order Items
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="font-semibold">Endgood Name</TableHead>
                        <TableHead className="font-semibold">SKU</TableHead>
                        <TableHead className="font-semibold text-right">Ordered Qty</TableHead>
                        <TableHead className="font-semibold text-right">Packed Qty</TableHead>
                        <TableHead className="font-semibold text-right">WIP Qty</TableHead>
                        <TableHead className="font-semibold text-right">Invoiced Qty</TableHead>
                        <TableHead className="font-semibold text-right">Pending Qty</TableHead>
                        <TableHead className="font-semibold text-right">Completion %</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detailData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                            No items found
                          </TableCell>
                        </TableRow>
                      ) : (
                        detailData.map((item, index) => {
                          const itemCompletion = item.orderedQuantity && item.orderedQuantity > 0
                            ? ((item.packedQuantity || 0) + (item.invoicedQuantity || 0)) / item.orderedQuantity * 100
                            : 0;
                          
                          return (
                            <TableRow key={`${item.salesOrderDetailId}-${index}`} className="hover:bg-gray-50">
                              <TableCell className="font-medium">
                                {item.endgoodName || 'N/A'}
                              </TableCell>
                              <TableCell>
                                {item.endgoodSku || 'N/A'}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatNumber(item.orderedQuantity)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatNumber(item.packedQuantity)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatNumber(item.wipQuantity)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatNumber(item.invoicedQuantity)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatNumber(item.pendingQuantity)}
                              </TableCell>
                              <TableCell className="text-right">
                                <span className={getCompletionColor(itemCompletion)}>
                                  {itemCompletion.toFixed(2)}%
                                </span>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default SalesOrderPerformanceDetailPage;

